import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import ThemeBadge from '../components/ThemeBadge';
import NewsBadges from '../components/NewsBadges';
import { LOCALITY_COUNTRIES, THEMES, formatDateTime } from '../theme';

const API_URL = process.env.REACT_APP_API_URL || '';
const LOCALITIES = Object.keys(LOCALITY_COUNTRIES);

function RelevanceBar({ score }) {
  const s = score ?? 0;
  const color = s >= 70 ? 'bg-emerald-500' : s >= 40 ? 'bg-amber-500' : 'bg-slate-300';
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full ${color}`} style={{ width: `${s}%` }} />
      </div>
      <span className="text-xs font-medium text-slate-500">{s}%</span>
    </div>
  );
}

function News({ user, onLogout }) {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') || '';
  const [theme, setTheme] = useState(params.get('theme') || '');
  const [locality, setLocality] = useState('');
  const country = params.get('country') || '';
  const [news, setNews] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [collecting, setCollecting] = useState(false);
  const [collectMsg, setCollectMsg] = useState('');
  const navigate = useNavigate();
  const token = localStorage.getItem('access_token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      if (q) {
        const { data } = await axios.get(`${API_URL}/api/search`, { params: { q, kind: 'news' } });
        setNews(data.news || []);
        setTotal((data.news || []).length);
      } else {
        const filters = {};
        if (theme) filters.theme = theme;
        if (locality) filters.locality = locality;
        if (country) filters.country = country;
        const { data } = await axios.get(`${API_URL}/api/news`, { params: filters });
        setNews(data.news || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, theme, locality, country]);

  const runCollect = async () => {
    setCollecting(true);
    setCollectMsg('');
    try {
      const { data } = await axios.post(`${API_URL}/api/news/collect`, {}, { headers });
      setCollectMsg(`${data.new_articles} notícia(s) nova(s) classificada(s) de ${data.fetched} buscadas.`);
      load();
    } catch (err) {
      setCollectMsg(err.response?.data?.detail || 'Falha ao coletar. Tente novamente.');
    } finally {
      setCollecting(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Notícias</h1>
            <p className="text-sm text-slate-500">
              {q ? (
                <>Resultados para “{q}” · <button className="text-brand-600 underline" onClick={() => setParams({})}>limpar busca</button></>
              ) : (
                'Coletadas do Google News nos 4 temas de pesquisa, classificadas por IA.'
              )}
            </p>
          </div>
          <button
            onClick={runCollect}
            disabled={collecting}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-60"
          >
            {collecting ? 'Coletando…' : '↻ Coletar agora'}
          </button>
        </div>

        {collectMsg && (
          <div className="rounded-lg bg-brand-50 px-4 py-2 text-sm text-brand-700">{collectMsg}</div>
        )}

        {!q && country && (
          <div className="text-sm text-slate-600">
            País: <strong>{country}</strong> ·{' '}
            <button className="text-brand-600 underline" onClick={() => setParams({})}>limpar filtro</button>
          </div>
        )}

        {!q && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setTheme('')}
              className={`rounded-full px-3 py-1.5 text-sm font-medium ring-1 ring-inset ${
                theme === '' ? 'bg-brand-600 text-white ring-brand-600' : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'
              }`}
            >
              Todos ({total})
            </button>
            {THEMES.map((t) => (
              <button
                key={t.value}
                onClick={() => setTheme(t.value)}
                className={`rounded-full px-3 py-1.5 text-sm font-medium ring-1 ring-inset ${
                  theme === t.value ? 'bg-brand-600 text-white ring-brand-600' : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'
                }`}
              >
                {t.label}
              </button>
            ))}
            <span className="mx-1 self-center text-slate-300">|</span>
            <select
              value={locality}
              onChange={(e) => setLocality(e.target.value)}
              className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600"
            >
              <option value="">Todas as localidades</option>
              {LOCALITIES.map((loc) => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-slate-400">Carregando…</p>
        ) : news.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-400">
            Nenhuma notícia encontrada. Clique em “Coletar agora” para buscar no Google News.
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-card">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-400">
                  <th className="px-4 py-3">Notícia</th>
                  <th className="px-4 py-3">Tema</th>
                  <th className="px-4 py-3">Relevância</th>
                  <th className="px-4 py-3">Localidade</th>
                  <th className="px-4 py-3">Fonte</th>
                  <th className="px-4 py-3">Data</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {news.map((n) => (
                  <tr
                    key={n.id}
                    onClick={() => navigate(`/news/${n.id}`)}
                    className="cursor-pointer hover:bg-slate-50"
                  >
                    <td className="max-w-md px-4 py-3">
                      <p className="line-clamp-2 text-sm font-medium text-slate-800">{n.title_pt || n.title}</p>
                      {n.title_pt && n.title_pt !== n.title && (
                        <p className="line-clamp-1 text-xs italic text-slate-400">Original: {n.title}</p>
                      )}
                      <NewsBadges article={n} className="mt-1.5" />
                      {n.classification_mock && (
                        <span className="mt-1 block text-xs italic text-amber-500">classificação em modo mock</span>
                      )}
                    </td>
                    <td className="px-4 py-3"><ThemeBadge value={n.theme} /></td>
                    <td className="px-4 py-3"><RelevanceBar score={n.relevance_score} /></td>
                    <td className="px-4 py-3 text-sm text-slate-500">{n.locality || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">{n.source}</td>
                    <td className="px-4 py-3 text-sm text-slate-400">{formatDateTime(n.published_at || n.fetched_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default News;
